import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Facebook, Linkedin, Twitter, Link2, FileWarning, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import Breadcrumbs from "@/components/common/Breadcrumbs";
import { Reveal } from "@/components/common/Reveal";
import BlogCard from "@/components/blog/BlogCard";
import EmptyState from "@/components/common/EmptyState";
import { Skeleton } from "@/components/ui/skeleton";
import { blogsService } from "@/services/blogsService";
import { useSite } from "@/context/SiteDataContext";
import RichContent from "@/components/common/RichContent";

export default function BlogDetail() {
  const SITE = useSite();
  const { slug } = useParams();
  const [post, setPost] = useState(undefined);
  const [related, setRelated] = useState([]);

  useEffect(() => {
    setPost(undefined);
    blogsService.getBlogPostBySlug(slug).then((p) => {
      setPost(p);
      if (p) blogsService.getRelatedPosts(p).then(setRelated);
    });
  }, [slug]);

  if (post === undefined) return <div className="container-x py-24 max-w-3xl mx-auto space-y-4"><Skeleton className="h-6 w-40" /><Skeleton className="h-14 w-full" /><Skeleton className="aspect-[16/9] w-full rounded-lg" /></div>;
  if (!post) return <div className="container-x py-24"><EmptyState icon={FileWarning} title="Article not found" description="This article is unavailable or may have been moved." actionLabel="Back to Blog" actionTo="/blog" /></div>;

  const date = new Date(post.date).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
  const shareUrl = typeof window !== "undefined" ? window.location.href : "";
  const copyLink = () => { navigator.clipboard?.writeText(shareUrl); toast.success("Link copied"); };

  return (
    <>
      <Seo title={post.seo?.title || post.title} description={post.seo?.description || post.excerpt} ogImage={post.seo?.ogImage || post.image} canonical={`https://${SITE.website}/blog/${post.slug}`} jsonLd={{ "@context": "https://schema.org", "@type": "BlogPosting", headline: post.title, image: post.image, datePublished: post.date, author: { "@type": "Organization", name: post.author } }} />

      <article>
        <div className="container-x pt-8">
          <Breadcrumbs crumbs={[{ label: "Blogs", to: "/blog" }, { label: post.title }]} />
        </div>
        <header className="container-x py-10 max-w-3xl mx-auto text-center">
          <Reveal>
            <div className="flex items-center justify-center gap-3 text-xs uppercase tracking-widest font-semibold text-greyish">
              <span className="text-aqua">{post.category}</span><span className="w-1 h-1 rounded-full bg-border" /><span>{date}</span><span className="w-1 h-1 rounded-full bg-border" /><span>{post.readTime}</span>
            </div>
            <h1 className="mt-5 text-3xl md:text-5xl font-bold tracking-tight text-ink text-balance">{post.title}</h1>
          </Reveal>
        </header>

        <div className="container-x">
          <Reveal>
            <div className="max-w-5xl mx-auto overflow-hidden rounded-lg aspect-[16/8] bg-[#f6f8f9]">
              <img src={post.image} alt={post.title} className="w-full h-full object-cover" />
            </div>
          </Reveal>
        </div>

        <div className="container-x py-14 max-w-3xl mx-auto">
          <RichContent blocks={post.content} />

          <div className="mt-12 pt-8 border-t border-border flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-greyish mr-1">Share</span>
              <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noopener noreferrer" data-testid="share-facebook" className="w-9 h-9 rounded-md border border-border flex items-center justify-center text-greyish hover:text-aqua hover:border-aqua transition-all"><Facebook className="w-4 h-4" /></a>
              <a href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(post.title)}`} target="_blank" rel="noopener noreferrer" data-testid="share-twitter" className="w-9 h-9 rounded-md border border-border flex items-center justify-center text-greyish hover:text-aqua hover:border-aqua transition-all"><Twitter className="w-4 h-4" /></a>
              <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noopener noreferrer" data-testid="share-linkedin" className="w-9 h-9 rounded-md border border-border flex items-center justify-center text-greyish hover:text-aqua hover:border-aqua transition-all"><Linkedin className="w-4 h-4" /></a>
              <button onClick={copyLink} data-testid="share-copy" className="w-9 h-9 rounded-md border border-border flex items-center justify-center text-greyish hover:text-aqua hover:border-aqua transition-all"><Link2 className="w-4 h-4" /></button>
            </div>
            <Link to="/contact" className="inline-flex items-center gap-1.5 text-sm font-semibold text-aqua">Have a question? Talk to our team <ArrowRight className="w-4 h-4" /></Link>
          </div>
        </div>
      </article>

      {related.length > 0 && (
        <section className="bg-[#f9fafb] border-t border-border">
          <div className="container-x py-16 md:py-20">
            <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-ink mb-8">Related Articles</h2>
            <div className="grid sm:grid-cols-2 gap-x-8 gap-y-10">{related.map((p, i) => <BlogCard key={p.id} post={p} index={i} />)}</div>
          </div>
        </section>
      )}
    </>
  );
}
